import React, { useEffect, useState } from "react";
import { Stack, Typography, Box } from "@mui/material";
import { Container } from "../../globalStyles";
import { useContributorsState, usePackagesActions, usePackagesState } from "@/store";
import { subscribeUploadCompletion } from "@/Service/uploadCompletionEvents";
import SearchPanel from "../Util/SearchPanel";
import PackageSection from "./PackageSection";
import Pagination from "../Util/Pagination";
import { PackageWrapper } from "./Package.elements";
import DataLoader from "../Loader/DataLoader/DataLoader";
import NoDataState from "@/components/EmptyData/NoDataState";
import { PackageViewSwitcher } from "./PackageViewSwitcher";
import { StatsSection, StatsTopBar, StatsTitleLeft, StatsDivider } from "../Stats/Counter/CountersBlock.elements";

const PackageGroupSelection = () => {
  const {
    packages,
    totalPages,
    packagesView,
    totalElements,
    fetch: { status } = {},
  } = usePackagesState();
  const { fetchPackages } = usePackagesActions();
  const { selectedContributorId, affiliate } = useContributorsState();

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    country: "",
    region: "",
    searchTerm: "",
    startDate: undefined,
    endDate: undefined,
  });
  // Bumped by the uploadCompletion subscription so the fetch effect below re-runs without
  // having to thread the bus into the params object. State (not ref) on purpose — we need
  // React to schedule the effect, and a ref wouldn't trigger it.
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const { searchTerm, country, region, startDate, endDate } = searchParams;
    fetchPackages({
      page: currentPage,
      pageSize,
      contributors: selectedContributorId,
      affiliate,
      packageName: searchTerm,
      country,
      region,
      fromDate: startDate,
      toDate: endDate,
    });
  }, [fetchPackages, currentPage, pageSize, selectedContributorId, affiliate, searchParams, refreshTick]);

  // Refresh the list when an upload finishes server-side (PUBLISHED / CAT_DV_INGESTED /
  // UPLOADED). Without this, the user has to flip pages to see their just-finished upload
  // appear, because the fetch effect only re-runs on filter/pagination changes.
  useEffect(() => subscribeUploadCompletion(() => setRefreshTick((t) => t + 1)), []);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 0 && pageNumber < totalPages) setCurrentPage(pageNumber);
  };

  const handlePageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const isLoading = status === "loading";
  const hasData = (packages?.length ?? 0) > 0;
  const totalPackages = totalElements ?? 0;

  return (
      
      <Container>
        <StatsSection>
          <StatsTopBar>
            <StatsTitleLeft>Uploads</StatsTitleLeft>
          </StatsTopBar>

          <SearchPanel
              setSearchParams={setSearchParams}
              hasCountryField
              hasRegionField
              searchLabel="Search Upload"
              searchPlaceholder="Enter package ID, upload ID, or description"
          />

          <StatsDivider />

          {!isLoading && hasData && (
              <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    px: { xs: 0, sm: 2 },
                    pt: 2,
                    pb: 2,
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
              >
                <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 1.5,
                      py: 1,
                      borderRadius: "12px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                >
                  <Typography
                      sx={{
                        color: "rgba(255,255,255,0.65)",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      }}
                  >
                    Total uploads
                  </Typography>
                  <Typography
                      sx={{
                        color: "#fff",
                        fontSize: "1rem",
                        fontWeight: 800,
                        lineHeight: 1,
                      }}
                  >
                    {totalPackages}
                  </Typography>
                </Box>

                <PackageViewSwitcher />
              </Stack>
          )}

          <style>{`
          @media (max-width: 1008px) {
            .uploads-items-scope svg,
            .uploads-items-scope canvas,
            .uploads-items-scope img {
              max-width: 100% !important;
              height: auto !important;
            }
          }
        `}</style>

          {isLoading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                <DataLoader />
              </div>
          ) : !hasData ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                <NoDataState />
              </div>
          ) : (
              <div className="uploads-items-scope">
                <PackageWrapper packagesView={packagesView}>
                  {packages.map((pkg) => (
                      <PackageSection
                          key={pkg.id}
                          uploadMetadataId={pkg.id}
                          upload={pkg}
                          packageName={pkg.packageName}
                          description={pkg.description}
                          contributorName={pkg.contributorName}
                          dateShot={new Date(pkg.createdAt * 1000).toLocaleDateString("en-GB", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                          status={pkg.status}
                          feedback={pkg.feedback}
                          stackFeedback={packagesView === "grid"}
                      />
                  ))}
                </PackageWrapper>

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
              </div>
          )}
        </StatsSection>
      </Container>
  );
};

export default PackageGroupSelection;